import React from 'react';

import cx from 'classnames';

import { Action } from '~shared/components/ActionMenu/ActionMenu';
import { IconButton } from '~shared/components/buttons';
import { TooltipV2 } from '~shared/components/TooltipV2';
import { proCheck } from '~teacher/util/proCheck';

import { Container } from '../Container/Container';

interface Props<T> {
  data?: Array<T> | null;
  actions: Array<Action<T>>;
}

function hasIcon<T>({ icon }: Action<T>) {
  return icon !== null && icon !== undefined;
}

function createMeetsConstraint<T>(data: Array<T>) {
  return ({ constraint }: Action<T>) => {
    // if there is no data to act on, show all options
    if (!data) return true;
    // run constraint function (removes item from visibility)
    if (typeof constraint !== 'function') return true;
    return constraint({ data });
  };
}

/**
 * Usage of the humble Action menu component using a row of icon buttons
 * * adds a field to the Action object: Icon (jsx component)
 * * the only available mode is clicking
 */
export function ActionButtons<T extends object>({
  actions,
  data = null,
}: Props<T>) {
  const meetsConstraint = createMeetsConstraint(data);
  const filteredActions = actions.filter(hasIcon).filter(meetsConstraint);

  return (
    <Container row>
      {filteredActions.map(
        ({
          icon,
          disabled,
          key,
          action,
          title,
          color,
          testId,
          paywallCallback,
        }) => (
          <ActionButton
            key={key}
            action={action}
            data={data}
            title={typeof title === 'function' ? title({ data }) : title}
            color={color}
            disabled={disabled}
            icon={icon}
            testId={testId}
            paywallCallback={paywallCallback}
          />
        )
      )}
    </Container>
  );
}

interface ActionButtonProps<T> {
  disabled: Action<T>['disabled'];
  title: string;
  data: Array<T>;
  action: Action<T>['action'];
  testId: string;
  color: string;
  icon: React.ReactNode;
  paywallCallback?: () => void;
}

function ActionButton<T>({
  title,
  disabled,
  data,
  action,
  testId,
  color,
  icon,
  paywallCallback,
}: ActionButtonProps<T>) {
  const isDisabled = disabled?.({ data });
  const className = cx(
    'mr-4',
    { ['cursor-default']: isDisabled, ['cursor-pointer']: !isDisabled },
    { ['opacity-50']: isDisabled, ['opacity-100']: !isDisabled }
  );
  const hasPaidPlan = proCheck.hasPaidPlan();

  return (
    <TooltipV2 label={title}>
      <div className={className}>
        <IconButton
          onClick={() => {
            if (paywallCallback && !hasPaidPlan) {
              paywallCallback();
              return true;
            }

            if (isDisabled) return true;

            return action({
              data,
              close: () => {},
            });
          }}
        >
          <span data-testid={testId ?? title} className={cx(color ?? '')}>
            {icon}
          </span>
        </IconButton>
      </div>
    </TooltipV2>
  );
}
